import React, { useMemo } from 'react';
import { ICON_COMPONENTS, LEGACY_ICON_PATH_TO_NEW_KEY } from './IconRegistry';

// ─── Props ──────────────────────────────────────────────────────────────────
// Pass exactly one of `name` or `path`:
//   name  – new asset path (e.g. 'settings/setting-2'). Preferred for new code.
//   path  – legacy KTSVG URL (e.g. '/media/icons/duotune/ecommerce/settings.svg').
//            Resolved via LEGACY_ICON_PATH_TO_NEW_KEY → ICON_COMPONENTS.
// If both are provided, `name` wins.

type NameProps = { name: string; path?: never };
type PathProps = { path: string; name?: never };

export type IconProps = (NameProps | PathProps) &
	React.SVGProps<SVGSVGElement> & {
		size?: number | string;
		className?: string;
	};

const DEFAULT_SIZE: React.CSSProperties['width'] = '1.25rem';

/** Substrings in the resolved new key → wrapper `color`. */
const COLOR_MARKERS: ReadonlyArray<readonly [pattern: string, color: string]> = [
	['-blue', '#00A3FF'],
];

/** Normalize a `name` prop to a registry key (no leading slash, no .svg). */
function normalizeNameKey(raw: string): string {
	return raw.trim().replace(/^\/+/, '').replace(/\.svg$/i, '');
}

/** Sanitize a legacy `path` prop to a bare duotune key for the legacy map lookup. */
function sanitizeLegacyPath(raw: string): string {
	let s = raw.trim();
	const qIdx = s.indexOf('?');
	if (qIdx !== -1) s = s.slice(0, qIdx);
	s = s.replace(/^\/+/, '');
	s = s.replace(/^public\//i, '');
	s = s.replace(/^media\/icons\//i, '');
	s = s.replace(/^icons\//i, '');
	return s;
}

function hasTextColorUtilityClass(className?: string): boolean {
	if (!className) return false;
	return /\btext-[\w-]+\b/.test(className);
}

function markerColorFromKey(key: string): string | undefined {
	for (const [pattern, hex] of COLOR_MARKERS) {
		if (key.includes(pattern)) return hex;
	}
	return undefined;
}

/**
 * Universal icon component. Pass `name` (new path) or `path` (legacy KTSVG URL).
 *
 * @example
 *   <Icon name="settings/setting-2" className="svg-icon-2" size="20px" />
 *   <Icon path="/media/icons/duotune/ecommerce/settings.svg" className="svg-icon-2" size="20px" />
 */
function IconInner({
	name,
	path,
	size = DEFAULT_SIZE,
	className,
	style,
	color,
	...svgProps
}: IconProps) {
	const resolvedKey = useMemo<string | null>(() => {
		if (name != null) {
			return normalizeNameKey(name);
		}
		if (path != null) {
			const legacy = sanitizeLegacyPath(path);
			return LEGACY_ICON_PATH_TO_NEW_KEY[legacy] ?? null;
		}
		return null;
	}, [name, path]);

	const SvgComp = resolvedKey != null ? ICON_COMPONENTS[resolvedKey] : undefined;

	const markerColor = useMemo(() => {
		if (color != null && color !== '') return undefined;
		if (hasTextColorUtilityClass(className)) return undefined;
		if (!resolvedKey) return undefined;
		// Check the resolved new key, and also the raw input in case it carried a color hint
		return markerColorFromKey(resolvedKey) ?? (path ? markerColorFromKey(path) : undefined);
	}, [color, className, resolvedKey, path]);

	const wrapperClass = ['svg-icon', className].filter(Boolean).join(' ');

	const wrapperStyle: React.CSSProperties = useMemo(
		() => ({
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center',
			width: size,
			height: size,
			lineHeight: 0,
			verticalAlign: 'middle',
			...(markerColor ? { color: markerColor } : {}),
			...style,
		}),
		[size, markerColor, style]
	);

	if (!SvgComp) {
		if (process.env.NODE_ENV === 'development') {
			const input = name != null ? `name="${name}"` : `path="${path}"`;
			const resolved = resolvedKey ?? '(no match in LEGACY_ICON_PATH_TO_NEW_KEY)';
			// eslint-disable-next-line no-console
			console.warn(`[Icon] No registry entry for ${input} → resolved key: "${resolved}"`);
		}
		return (
			<span
				className={wrapperClass}
				style={{
					display: 'inline-block',
					width: size,
					height: size,
					background: 'currentColor',
					opacity: 0.15,
					borderRadius: 2,
					...style,
				}}
				aria-hidden
			/>
		);
	}

	return (
		<span className={wrapperClass} style={wrapperStyle} aria-hidden>
			<SvgComp
				fill='currentColor'
				width='100%'
				height='100%'
				color={color}
				{...svgProps}
				style={{ width: '100%', height: '100%' }}
			/>
		</span>
	);
}

export const Icon = React.memo(IconInner);
Icon.displayName = 'Icon';
